import { ArrowLeft, LockSimple } from '@phosphor-icons/react';
import {
  useCreateCheckout,
  useGetSubscription,
  useListPlans,
} from '@reelplicate/api-client/billing';
import { OnboardingStep } from '@reelplicate/api-client/model';
import { useListWorkspaces } from '@reelplicate/api-client/workspaces';
import { Button, Divider, Inline, Link, Skeleton, Stack, Well } from '@reelplicate/ui';
import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router';

import { useCheckout } from '@/lib/billing/use-checkout';

import { OnboardingCard, OnboardingHeader } from './onboarding-card';
import { useOnboardingProgress } from './progress';
import { ONBOARDING_STEPS, stepPath } from './steps';

/** How long to wait for the provider's webhook before moving on anyway. */
const CONFIRM_TIMEOUT_MS = 15_000;

const dateFormat = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});
const money = (amount: number) => `$${amount.toFixed(2)}`;

/**
 * Figma "Onboarding — 5b · Payment" (335:1339): the payment provider's inline
 * checkout beside an order summary of the plan saved on step 5, inside a 960
 * form card. The provider takes the card (and declines it, inside its frame);
 * on a period with a trial nothing is charged until it ends, otherwise the
 * first period is charged now. Once it's done we wait for its webhook to
 * confirm the subscription, then move on to Team. With
 * no plan saved yet it sends people back to choose one; on a server without
 * payments it just carries on.
 */
export function PaymentPage() {
  const navigate = useNavigate();
  const { advance } = useOnboardingProgress();
  const [finishing, setFinishing] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  // Before checkout the trial hasn't started: it would end this long from now.
  const [openedAt] = useState(() => Date.now());
  const { data: workspaces } = useListWorkspaces();
  const workspaceId = workspaces?.[0]?.id;
  const { data: plans } = useListPlans();
  // No plan saved is expected (404): handled here, not by the error boundary.
  const { data: subscription, error: noPlan } = useGetSubscription(workspaceId ?? '', {
    query: {
      enabled: Boolean(workspaceId),
      retry: false,
      throwOnError: false,
      // After checkout, until the provider's webhook has landed.
      refetchInterval: finishing ? 1000 : false,
    },
  });
  useEffect(() => {
    if (noPlan) void navigate(stepPath(5), { replace: true });
  }, [noPlan, navigate]);

  const needsCheckout =
    subscription?.paymentsEnabled && subscription.status === 'INCOMPLETE';
  const checkout = useCreateCheckout();
  const { mutate: openCheckout } = checkout;
  // One checkout per visit, even when effects run twice.
  const requested = useRef(false);
  useEffect(() => {
    if (!workspaceId || !needsCheckout || requested.current) return;
    requested.current = true;
    openCheckout({ id: workspaceId });
  }, [workspaceId, needsCheckout, openCheckout]);

  const {
    mountOn: frameTarget,
    status: frameStatus,
    error: frameError,
    totals,
  } = useCheckout(checkout.data, () => setFinishing(true));

  const confirmed = subscription !== undefined && subscription.status !== 'INCOMPLETE';
  useEffect(() => {
    if (!finishing) return;
    const timer = setTimeout(() => setTimedOut(true), CONFIRM_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [finishing]);
  useEffect(() => {
    if (!finishing || !(confirmed || timedOut)) return;
    void (async () => {
      if (await advance(OnboardingStep.TEAM)) await navigate(stepPath(6));
      else setFinishing(false);
    })();
  }, [finishing, confirmed, timedOut, advance, navigate]);

  const plan = plans?.find((p) => p.id === subscription?.plan);
  const yearly = subscription?.billingPeriod === 'YEARLY';
  const period = yearly ? 'a year' : 'a month';
  const trialDays = (yearly ? plan?.yearlyTrialDays : plan?.monthlyTrialDays) ?? 0;
  const hasTrial = trialDays > 0;
  const trialEnds = subscription?.trialEndsAt
    ? new Date(subscription.trialEndsAt)
    : new Date(openedAt + trialDays * 86_400_000);
  // Without a trial the first period is paid today; the next charge is a period on.
  const renews = new Date(openedAt);
  if (yearly) renews.setFullYear(renews.getFullYear() + 1);
  else renews.setMonth(renews.getMonth() + 1);

  return (
    <OnboardingCard
      loose
      onSubmit={(event) => {
        event.preventDefault();
        setFinishing(true);
      }}
      aria-busy={finishing || undefined}
      className="max-w-240"
    >
      <OnboardingHeader
        eyebrow={`Step 5 of ${ONBOARDING_STEPS.length}`}
        title="Add a payment method"
        subtitle={
          !plan || !subscription
            ? 'Cancel anytime in Settings → Billing & plan.'
            : hasTrial
              ? `You won’t be charged today. Your ${plan.name} trial runs until ${dateFormat.format(trialEnds)}, then it’s ${money(subscription.price).replace('.00', '')} ${period}.`
              : `Your ${plan.name} plan is ${money(subscription.price).replace('.00', '')} ${period}, starting today.`
        }
      />

      <Stack gap={8} className="laptop:flex-row laptop:items-start">
        <Stack gap={4} className="laptop:w-[520px] laptop:shrink-0">
          {subscription && !subscription.paymentsEnabled ? (
            <Well tone="info" title="Payments are off on this server">
              Your {hasTrial ? 'trial' : 'plan'} starts without a card. Add one later in
              Settings → Billing & plan.
            </Well>
          ) : subscription && !needsCheckout && !finishing ? (
            <Well tone="success" title="Your payment method is saved">
              Carry on to set up your team.
            </Well>
          ) : (
            <>
              {finishing ? (
                <p role="status" className="text-body-md text-text-secondary">
                  {hasTrial ? 'Starting your trial…' : 'Starting your plan…'}
                </p>
              ) : frameError ? (
                <Well tone="danger" role="alert" title="The payment form didn’t load">
                  {frameError}
                </Well>
              ) : null}
              {frameStatus === 'loading' && !frameError && (
                <Stack gap={3} aria-busy="true">
                  <span className="sr-only" role="status">
                    Loading the payment form…
                  </span>
                  <Skeleton shape="rectangle" shimmer className="h-12" />
                  <Skeleton shape="rectangle" shimmer className="h-12" />
                  <Skeleton shape="rectangle" shimmer className="h-40" />
                </Stack>
              )}
              {/* The provider's checkout frame is mounted in here. */}
              <div
                ref={frameTarget}
                data-testid="checkout-frame"
                className={frameStatus === 'loading' ? 'h-0 overflow-hidden' : undefined}
              />
            </>
          )}
        </Stack>

        <Stack
          as="aside"
          gap={4}
          aria-label="Order summary"
          className="bg-bg-subtle min-w-0 flex-1 rounded-lg p-6"
        >
          {plan && subscription ? (
            <>
              {/* Change sits on the plan name's baseline, not the block's middle. */}
              <Inline gap={2} alignY="baseline">
                <Stack gap={0.5} className="min-w-0 flex-1">
                  <p className="text-heading-h4 text-text-primary">{plan.name}</p>
                  <p className="text-body-sm text-text-secondary">
                    {plan.seats} {plan.seats === 1 ? 'seat' : 'seats'} · up to{' '}
                    {plan.videosPerMonth} videos a month
                  </p>
                </Stack>
                {needsCheckout && (
                  <Link asChild size="sm">
                    <RouterLink to={stepPath(5)}>Change</RouterLink>
                  </Link>
                )}
              </Inline>
              <Divider />
              <SummaryLine
                label={`${plan.name}, billed ${yearly ? 'yearly' : 'monthly'}`}
                amount={money(subscription.price)}
              />
              {hasTrial && (
                <SummaryLine
                  label={`${trialDays}-day free trial`}
                  amount={`−${money(subscription.price)}`}
                  brand
                />
              )}
              {/* Tax comes from the provider once it knows the country. */}
              {totals && totals.tax > 0 && (
                <SummaryLine label="Tax" amount={money(totals.tax)} />
              )}
              <Divider />
              <Inline gap={2}>
                <p className="text-body-md-strong text-text-primary flex-1">Due today</p>
                <p className="text-heading-h3 text-text-primary">
                  {money(totals ? totals.total : hasTrial ? 0 : subscription.price)}
                </p>
              </Inline>
              <p className="text-caption text-text-secondary">
                {hasTrial ? 'Then' : 'Renews at'}{' '}
                {money(totals?.recurringTotal ?? subscription.price)} {period} from{' '}
                {dateFormat.format(hasTrial ? trialEnds : renews)}
                {totals?.recurringTotal != null
                  ? ', tax included'
                  : ', plus any sales tax'}
                . Cancel anytime in Settings → Billing & plan.
              </p>
            </>
          ) : (
            <Stack gap={3} aria-busy="true">
              <span className="sr-only" role="status">
                Loading your plan…
              </span>
              <Skeleton shape="rectangle" className="h-12" />
              <Skeleton shape="rectangle" className="h-28" />
            </Stack>
          )}
          <Inline gap={1.5} alignY="top">
            <LockSimple
              aria-hidden
              className="text-icon-secondary mt-px size-3.5 shrink-0"
            />
            <p className="text-caption text-text-secondary">
              Checkout is run by Paddle, our reseller. We never see your card details.
            </p>
          </Inline>
        </Stack>
      </Stack>

      <Inline gap={3}>
        <Button
          asChild={!finishing}
          variant="ghost"
          size="lg"
          leadingIcon={ArrowLeft}
          disabled={finishing}
        >
          {finishing ? 'Back' : <RouterLink to={stepPath(5)}>Back</RouterLink>}
        </Button>
        {subscription && !needsCheckout && (
          <Button type="submit" size="lg" loading={finishing} className="ml-auto">
            Continue
          </Button>
        )}
      </Inline>
    </OnboardingCard>
  );
}

function SummaryLine({
  label,
  amount,
  brand = false,
}: {
  label: string;
  amount: string;
  brand?: boolean;
}) {
  return (
    <Inline gap={2}>
      <p className="text-body-md text-text-secondary flex-1">{label}</p>
      <p
        className={
          brand ? 'text-data-md text-text-brand' : 'text-data-md text-text-primary'
        }
      >
        {amount}
      </p>
    </Inline>
  );
}
